import { useEffect, useRef, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";

import { useMaterialUIController, setMiniSidenav } from "context";

function Sidenav({ color, brand, brandName, routes, sidenavMode, onCycleMode, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();

  const theme = useTheme();
  // true = drawer sits beside the content; false = it is an off-canvas overlay.
  const isDocked = useMediaQuery(theme.breakpoints.up("navOverlay"));

  // Scroll preservation across miniSidenav transitions
  const paperRef = useRef(null);
  const savedScrollTopRef = useRef(0);
  const prevMiniSidenav = useRef(miniSidenav);

  useEffect(() => {
    if (prevMiniSidenav.current === miniSidenav) return;
    prevMiniSidenav.current = miniSidenav;
    const paper = paperRef.current;
    if (!paper) return;
    const timer = setTimeout(() => {
      paper.scrollTop = savedScrollTopRef.current;
    }, 320);
    return () => clearTimeout(timer);
  }, [miniSidenav]);

  // Click-to-expand submenu state (open state kept while navigating — replaces hover flyout).
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [miniSubmenu, setMiniSubmenu] = useState({ anchorEl: null, item: null });

  useEffect(() => {
    if (!miniSidenav && miniSubmenu.anchorEl) {
      setMiniSubmenu({ anchorEl: null, item: null });
    }
  }, [miniSidenav, miniSubmenu.anchorEl]);

  // Auto-expand the parent that contains the active route.
  useEffect(() => {
    const activeParents = routes
      .filter((r) => r.collapse && r.collapse.some((c) => c.route && location.pathname === c.route))
      .map((r) => r.key);
    if (activeParents.length) {
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        activeParents.forEach((k) => next.add(k));
        return next;
      });
    }
  }, [location.pathname, routes]);

  const toggleExpand = (key) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const handleNavItemClick = () => {
    if (!isDocked) closeSidenav();
  };

  const dividerLight =
    (!darkMode && !whiteSidenav && !transparentSidenav) ||
    (darkMode && !transparentSidenav && whiteSidenav);

  // Recursive: parent (collapse) → click-to-expand inline children with a tree guide line.
  const renderItem = (item, depth = 0) => {
    const { type, name, icon, title, key, href, route, collapse } = item;

    if (type === "title") {
      return (
        <MDTypography
          key={key}
          color="dark"
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    }

    if (type === "divider") {
      return <Divider key={key} light={dividerLight} />;
    }

    if (type !== "collapse") return null;

    const hasChildren = Array.isArray(collapse) && collapse.length > 0;

    if (hasChildren) {
      const isExpanded = expandedKeys.has(key);
      const childActive = collapse.some((c) => c.route && location.pathname === c.route);
      const children = collapse.map((child) => renderItem(child, depth + 1));

      if (miniSidenav) {
        return (
          <div key={key}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={childActive}
              isParent
              expanded={Boolean(miniSubmenu.anchorEl) && miniSubmenu.item?.key === key}
              aria-haspopup="menu"
              aria-expanded={Boolean(miniSubmenu.anchorEl) && miniSubmenu.item?.key === key}
              onClick={(event) =>
                setMiniSubmenu({
                  anchorEl: event.currentTarget,
                  item,
                })
              }
            />
          </div>
        );
      }

      return (
        <div key={key}>
          <SidenavCollapse
            name={name}
            icon={icon}
            active={childActive}
            isParent
            expanded={isExpanded}
            onClick={() => toggleExpand(key)}
          />
          {isExpanded && (
            <MDBox
              sx={(t) => ({
                ml: 2.5,
                pl: 0.5,
                borderLeft: `1px solid ${t.palette.borderColor.main}`,
              })}
            >
              {children}
            </MDBox>
          )}
        </div>
      );
    }

    // Leaf item
    const active = location.pathname === route;
    return href ? (
      <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
        <SidenavCollapse name={name} icon={icon} active={active} />
      </Link>
    ) : (
      <NavLink key={key} to={route} onClick={handleNavItemClick}>
        <SidenavCollapse name={name} icon={icon} active={active} />
      </NavLink>
    );
  };

  const renderRoutes = (routesList) => routesList.map((item) => renderItem(item, 0));

  // One string for both the tooltip and the aria-label, so the icon-only mode button
  // announces the action it will perform.
  const modeActionLabel =
    sidenavMode === "auto"
      ? "Menüyü sabitle"
      : sidenavMode === "pinned"
      ? "Menüyü gizle"
      : "Menüyü göster";

  return (
    <>
      {/* Below navOverlay the drawer covers the page (variant="permanent" ships no backdrop),
          so add a dismiss scrim — otherwise the only way out is the navbar hamburger. */}
      {!isDocked && !miniSidenav && (
        <MDBox
          onClick={closeSidenav}
          sx={(t) => ({
            position: "fixed",
            inset: 0,
            zIndex: t.zIndex.drawer - 1,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          })}
        />
      )}
      <SidenavRoot
        {...rest}
        variant="permanent"
        ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
        PaperProps={{
          "aria-label": "Ana navigasyon",
        }}
      >
        {/* px must shrink in mini: a 32px gutter left 16px of content box on the 80px rail,
          which squeezed the 40px wordmark. */}
        <MDBox pt={3} pb={1} px={miniSidenav ? 1 : 4} textAlign="center">
          <MDBox
            display={{ xs: "block", navOverlay: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </MDTypography>
          </MDBox>
          <MDBox
            component={NavLink}
            to="/"
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={handleNavItemClick}
          >
            {brand && (
              <MDBox
                component="img"
                src={brand}
                alt="Brand"
                width={miniSidenav ? "2.5rem" : "5rem"}
                sx={{ transition: "width 0.3s ease" }}
              />
            )}
          </MDBox>
        </MDBox>
        <Divider light={dividerLight} />
        <MDBox
          ref={paperRef}
          onScroll={(event) => {
            savedScrollTopRef.current = event.currentTarget.scrollTop;
          }}
          sx={(t) => ({
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            // Thin quiet scrollbar: the platform default (17px, with arrow buttons) ate a fifth
            // of the 80px rail and visibly shifted the icon column.
            // On the 80px rail the scrollbar steals width on the right only, so mirror it on the
            // left to keep the icon column centred (matches the 6px webkit thumb below).
            pl: miniSidenav ? "6px" : 0,
            scrollbarWidth: "thin",
            scrollbarColor: `${t.functions.rgba(t.palette.ink.main, 0.2)} transparent`,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 3,
              backgroundColor: t.functions.rgba(t.palette.ink.main, 0.2),
            },
          })}
        >
          {/* Row gutter lives here so rows span the full inner width and stay centred in the rail. */}
          <List sx={{ pt: 0, pb: 1, px: miniSidenav ? 1 : 1.5 }}>{renderRoutes(routes)}</List>
        </MDBox>
        <Menu
          anchorEl={miniSubmenu.anchorEl}
          open={Boolean(miniSubmenu.anchorEl)}
          onClose={() => setMiniSubmenu({ anchorEl: null, item: null })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: (t) => ({
              ml: 1,
              minWidth: 220,
              maxHeight: "min(70vh, 560px)",
              border: `1px solid ${t.palette.borderColor.main}`,
            }),
          }}
        >
          <MDBox px={1.5} py={1}>
            <MDTypography variant="caption" fontWeight="bold" color="text">
              {miniSubmenu.item?.name}
            </MDTypography>
          </MDBox>
          <Divider sx={{ my: 0 }} />
          {(miniSubmenu.item?.collapse || []).map((child) => (
            <MenuItem
              key={child.key}
              component={child.href ? Link : NavLink}
              href={child.href}
              to={child.route}
              selected={Boolean(child.route && location.pathname === child.route)}
              onClick={() => {
                setMiniSubmenu({ anchorEl: null, item: null });
                handleNavItemClick();
              }}
              sx={{ minHeight: 40 }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>{child.icon}</ListItemIcon>
              {child.name}
            </MenuItem>
          ))}
        </Menu>
        {onCycleMode && (
          <MDBox
            px={1.5}
            py={1}
            display="flex"
            justifyContent={miniSidenav ? "center" : "flex-start"}
            sx={(t) => ({
              flex: "0 0 auto",
              backgroundColor: t.palette.background.sidenav,
              borderTop: `1px solid ${t.palette.borderColor.main}`,
            })}
          >
            <Tooltip title={modeActionLabel} placement="right">
              <Button
                size="small"
                aria-label={modeActionLabel}
                onClick={onCycleMode}
                sx={(t) => ({
                  minWidth: miniSidenav ? 40 : 0,
                  px: miniSidenav ? 0.75 : 1,
                  textTransform: "none",
                  color: t.palette.ink.secondary,
                  "& .MuiButton-startIcon": { marginRight: miniSidenav ? 0 : 8 },
                  "&:hover": { color: t.palette.accent.onSurface },
                })}
                startIcon={
                  <Icon fontSize="small">
                    {sidenavMode === "auto"
                      ? "push_pin"
                      : sidenavMode === "pinned"
                      ? "visibility_off"
                      : "visibility"}
                  </Icon>
                }
              >
                {miniSidenav ? null : modeActionLabel}
              </Button>
            </Tooltip>
          </MDBox>
        )}
      </SidenavRoot>
    </>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
  sidenavMode: "auto",
  onCycleMode: null,
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  sidenavMode: PropTypes.string,
  onCycleMode: PropTypes.func,
};

export default Sidenav;
